/**
 * NotesView -- Recopilador de notas del usuario (bugs / mejoras / ideas / general / question)
 * con adjuntos (imagenes + PDF). Consume PANEL-NOTES-V1-001 T2 endpoints.
 *
 * Layout:
 *   - Header con boton "Nueva nota" + filtros (category, status, priority)
 *   - Lista de notas (cards) con thumbnail del primer attachment si imagen
 *   - Click en una nota -> panel de detalle a la derecha (form de edicion + attachments)
 *
 * @module presentation/views/NotesView
 */

import { NotesApiClient } from '../../infrastructure/api/NotesApiClient.js';
import { getSelectedBusiness } from '../components/BusinessSelector.js';

const CATEGORIES = ['bug', 'improvement', 'idea', 'general', 'question'];
const PRIORITIES = ['low', 'medium', 'high', 'critical'];
const STATUSES   = ['open', 'wip', 'resolved', 'discarded'];

const CATEGORY_LABEL = {
  bug:         '🐛 Bug',
  improvement: '⚡ Mejora',
  idea:        '💡 Idea',
  general:     '📝 General',
  question:    '❓ Pregunta'
};
const PRIORITY_COLOR = {
  low:      '#6e7681',
  medium:   '#58a6ff',
  high:     '#d29922',
  critical: '#f85149'
};
const STATUS_LABEL = {
  open:      'Abierta',
  wip:       'En progreso',
  resolved:  'Resuelta',
  discarded: 'Descartada'
};

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc as escHtml } from '../shared/texto.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

export function NotesView() {
  // Estado local
  let notes = [];
  let totalCount = 0;
  let page = 1;
  const pageSize = 20;
  let filterCategory = '';
  let filterStatus = '';
  let filterPriority = '';
  let selectedNote = null;
  let loading = false;
  let saving = false;

  // Cache de blob URLs para revoke al destruir vista
  const blobUrls = new Set();
  // Cache attId -> blobUrl para evitar re-fetch en cada render (regresion PR#24:
  // reload() post-save reescribia container.innerHTML y se perdian los <img src=blobUrl>
  // de hydrateThumbnails. Cachear permite re-asignar src instantaneo en cada render).
  const thumbCache = new Map();

  const container = document.createElement('div');
  container.className = 'view-container notes-view';

  async function reload() {
    loading = true;
    render();
    const res = await NotesApiClient.list({
      page,
      pageSize,
      category: filterCategory || undefined,
      status: filterStatus || undefined,
      priority: filterPriority || undefined
    });
    loading = false;
    if (res.ok) {
      const payload = res.data?.data ?? res.data;
      notes = Array.isArray(payload?.items) ? payload.items : [];
      totalCount = payload?.totalCount ?? notes.length;
    } else {
      notes = [];
      totalCount = 0;
      console.warn('[NotesView] list error', res.error);
    }
    render();
  }

  async function loadDetail(noteId) {
    const res = await NotesApiClient.getById(noteId);
    if (res.ok) {
      const payload = res.data?.data ?? res.data;
      selectedNote = payload;
      render();
      hydrateThumbnails();
    } else {
      console.warn('[NotesView] getById error', res.error);
    }
  }

  async function hydrateThumbnails() {
    if (!selectedNote?.attachments) return;
    const noteId = selectedNote.id;
    for (const att of selectedNote.attachments) {
      if (!att.thumbnailUrl) continue;
      // Reusar blob URL cacheado si ya lo descargamos: re-render solo re-asigna src.
      let url = thumbCache.get(att.id);
      if (!url) {
        // #2495 NOTES T7: capturar status para distinguir 410 GONE (attachment legacy).
        const meta = {};
        url = await NotesApiClient.fetchAttachmentBlobUrl(att.noteId, att.id, { thumb: true, meta });
        // Si la nota seleccionada cambio mientras esperabamos, descartar resultado
        if (selectedNote?.id !== noteId) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        if (!url) {
          // 410 GONE = attachment anterior al fix de storage backend -> permanente.
          if (meta.status === 410) markAttachmentGone(att.id, 'no disponible');
          continue;
        }
        thumbCache.set(att.id, url);
        blobUrls.add(url);
      }
      if (url) {
        const img = container.querySelector(`img[data-att-id="${att.id}"]`);
        if (img && img.src !== url) img.src = url;
      }
    }
  }

  // #2495 NOTES T7: reemplaza el thumbnail roto por un placeholder claro (410 legacy).
  function markAttachmentGone(attId, label) {
    const thumb = container.querySelector(`.att-thumb[data-att-id="${attId}"]`);
    if (!thumb) return;
    thumb.style.cursor = 'default';
    thumb.removeAttribute('data-att-clickable');
    const img = thumb.querySelector(`img[data-att-id="${attId}"]`);
    if (img) img.remove();
    if (thumb.querySelector('[data-att-gone]')) return;
    const ph = document.createElement('div');
    ph.setAttribute('data-att-gone', '1');
    ph.style.cssText = 'position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;color:#6e7681;text-align:center;padding:4px;';
    ph.innerHTML = `<span style="font-size:20px;opacity:.7">🖼️</span><span style="font-size:0.875rem;line-height:1.2">${escHtml(label || 'no disponible')}</span>`;
    thumb.appendChild(ph);
  }

  function render() {
    container.innerHTML = `
      <div class="view-header">
        <div class="view-header-top">
          <h2 class="view-title">📝 Notas</h2>
          <span class="view-subtitle">Bugs · Mejoras · Ideas</span>
        </div>
        <div class="view-actions" style="display:flex; gap:8px; align-items:center; margin-top:8px;">
          <button id="notes-new-btn" class="btn-primary">+ Nueva nota</button>
          <select id="notes-flt-cat" class="form-control" style="width:auto;">
            <option value="">Categoria: todas</option>
            ${CATEGORIES.map(c => `<option value="${c}" ${filterCategory === c ? 'selected' : ''}>${CATEGORY_LABEL[c]}</option>`).join('')}
          </select>
          <select id="notes-flt-status" class="form-control" style="width:auto;">
            <option value="">Estado: todos</option>
            ${STATUSES.map(s => `<option value="${s}" ${filterStatus === s ? 'selected' : ''}>${STATUS_LABEL[s]}</option>`).join('')}
          </select>
          <select id="notes-flt-priority" class="form-control" style="width:auto;">
            <option value="">Prioridad: todas</option>
            ${PRIORITIES.map(p => `<option value="${p}" ${filterPriority === p ? 'selected' : ''}>${p}</option>`).join('')}
          </select>
          <span style="color:#8b949e; font-size:0.875rem; margin-left:auto;">${totalCount} nota(s)</span>
        </div>
      </div>

      <div class="notes-layout" style="display:grid; grid-template-columns: repeat(auto-fit, minmax(min(320px, 100%), 1fr)); gap:16px; margin-top:16px;">
        <section class="notes-list">
          ${loading ? '<div class="empty-state"><p>Cargando…</p></div>' : renderListBody()}
        </section>
        <section class="notes-detail">
          ${renderDetailPanel()}
        </section>
      </div>
    `;
    bindEvents();
    // Re-hidratar thumbnails despues de cada render. innerHTML reescribe los <img>
    // dejandolos con src="", asi cualquier reload() / save / filter cambio fuerza re-render
    // y los thumbnails desaparecerian sin esto. thumbCache evita re-fetch en cada render.
    if (selectedNote?.attachments?.some(a => a.mimeType?.startsWith('image/'))) {
      hydrateThumbnails();
    }
  }
  // Nota: toast + modal-root viven en document.body (no en container.innerHTML)
  // para que sobrevivan a re-renders del render() que reescribe el innerHTML.

  function renderListBody() {
    if (notes.length === 0) {
      return '<div class="empty-state"><p>Sin notas. ¡Crea la primera!</p></div>';
    }
    return `
      <div class="notes-cards" style="display:flex; flex-direction:column; gap:8px;">
        ${notes.map(n => {
          const isActive = selectedNote?.id === n.id;
          const tags = Array.isArray(n.tags) ? n.tags : [];
          return `
          <article class="note-card ${isActive ? 'note-card-active' : ''}"
                   data-note-id="${escHtml(n.id)}"
                   style="background:${isActive ? '#1c2530' : '#161b22'};
                          border:1px solid ${isActive ? '#58a6ff' : '#30363d'};
                          border-left:3px solid ${PRIORITY_COLOR[n.priority] || '#30363d'};
                          border-radius:8px; padding:12px 14px; cursor:pointer;
                          transition:border-color 0.15s, background 0.15s;">
            <div style="display:flex; justify-content:space-between; align-items:start; gap:8px;">
              <h3 style="margin:0; font-size:0.98rem; color:#f0f6fc; font-weight:600; line-height:1.3; word-break:break-word;">
                ${escHtml(n.title)}
              </h3>
              <span title="prioridad ${escHtml(n.priority)}"
                    style="font-size:0.875rem; color:${PRIORITY_COLOR[n.priority] || '#8b949e'}; white-space:nowrap; flex-shrink:0;">
                ● ${escHtml(n.priority)}
              </span>
            </div>

            <div style="display:flex; gap:8px; flex-wrap:wrap; margin:8px 0; font-size:0.875rem; color:#8b949e; align-items:center;">
              <span>${escHtml(CATEGORY_LABEL[n.category] || n.category)}</span>
              <span style="color:#30363d;">·</span>
              <span>${escHtml(STATUS_LABEL[n.status] || n.status)}</span>
              ${n.attachmentCount > 0 ? `<span style="color:#30363d;">·</span><span title="${n.attachmentCount} adjunto(s)">📎 ${n.attachmentCount}</span>` : ''}
            </div>

            ${n.body ? `<p style="margin:0 0 8px; font-size:0.875rem; color:#c9d1d9; opacity:0.85; line-height:1.45;">${escHtml(n.body.length > 140 ? n.body.slice(0, 140) + '…' : n.body)}</p>` : ''}

            ${tags.length > 0 ? `<div style="display:flex; flex-wrap:wrap; gap:4px; margin-bottom:8px;">${tags.slice(0, 4).map(t => `<span style="background:#1f6feb22; color:#79c0ff; padding:1px 7px; border-radius:8px; font-size:0.875rem;">#${escHtml(t)}</span>`).join('')}${tags.length > 4 ? `<span style="color:#6e7681; font-size:0.875rem;">+${tags.length - 4}</span>` : ''}</div>` : ''}

            <p style="margin:0; font-size:0.875rem; color:#6e7681;">
              ${formatDate(n.createdAt)}
            </p>
          </article>
        `;}).join('')}
      </div>
    `;
  }

  function renderDetailPanel() {
    if (!selectedNote) {
      return `
        <div class="empty-state" style="padding:40px; text-align:center; color:#8b949e;">
          <p>Selecciona una nota para ver el detalle</p>
        </div>
      `;
    }
    const n = selectedNote;
    const fieldStyle = 'display:flex; flex-direction:column; gap:6px;';
    const labelStyle = 'font-size:0.875rem; color:#8b949e; font-weight:500;';
    const ctrlStyle  = 'width:100%; box-sizing:border-box;';
    const tags = Array.isArray(n.tags) ? n.tags : [];
    const meta = (n.metadata && typeof n.metadata === 'object') ? n.metadata : {};
    const linkedMissionId = meta.converted_to_mission_id || null;
    const linkedMissionCode = meta.converted_to_mission_code || null;

    return `
      <article class="note-detail" style="background:#161b22; border:1px solid #30363d; border-radius:8px; padding:18px;">
        <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:8px; margin-bottom:14px;">
          <div style="min-width:0; flex:1;">
            <div style="font-size:0.875rem; color:#6e7681; text-transform:uppercase; letter-spacing:0.05em;">
              ${formatDate(n.createdAt)} · creada
            </div>
            <h3 style="margin:4px 0 0; color:#f0f6fc; font-size:1.1rem; word-break:break-word;">
              ${escHtml(n.title)}
            </h3>
          </div>
          <div style="display:flex; gap:6px; flex-shrink:0;">
            ${linkedMissionId ? '' : `<button id="note-convert-btn" class="btn-primary"
                  style="font-size:0.875rem; white-space:nowrap;" title="Crear mision con title/body/adjuntos de esta nota">🚀 Convertir a mision</button>`}
            <button id="note-delete-btn" class="btn-secondary"
                    style="font-size:0.875rem; white-space:nowrap;">🗑 Descartar</button>
          </div>
        </div>

        ${linkedMissionId ? `
          <div style="margin-bottom:14px; padding:10px 12px; background:#1f6feb22; border:1px solid #1f6feb55; border-radius:6px; display:flex; align-items:center; gap:8px; font-size:0.875rem; color:#79c0ff;">
            <span style="font-size:1rem;">🔗</span>
            <span>Convertida a mision</span>
            <a href="#/missions/${escHtml(linkedMissionId)}" style="color:#79c0ff; text-decoration:underline; font-family:monospace;">${escHtml(linkedMissionCode || linkedMissionId)}</a>
          </div>
        ` : ''}

        <div style="display:flex; gap:6px; flex-wrap:wrap; margin-bottom:14px;">
          <span style="background:#21262d; padding:3px 10px; border-radius:12px; font-size:0.875rem; color:#c9d1d9;">
            ${escHtml(CATEGORY_LABEL[n.category] || n.category)}
          </span>
          <span style="background:${PRIORITY_COLOR[n.priority] || '#6e7681'}22; padding:3px 10px; border-radius:12px; font-size:0.875rem; color:${PRIORITY_COLOR[n.priority] || '#8b949e'};">
            ● ${escHtml(n.priority)}
          </span>
          <span style="background:#21262d; padding:3px 10px; border-radius:12px; font-size:0.875rem; color:#c9d1d9;">
            ${escHtml(STATUS_LABEL[n.status] || n.status)}
          </span>
          ${tags.map(t => `<span style="background:#1f6feb33; padding:3px 10px; border-radius:12px; font-size:0.875rem; color:#79c0ff;">#${escHtml(t)}</span>`).join('')}
        </div>

        <div style="display:flex; flex-direction:column; gap:14px;">
          <div style="${fieldStyle}">
            <span style="${labelStyle}">Titulo</span>
            <input id="note-title" class="form-control" value="${escHtml(n.title)}" maxlength="200"
                   style="${ctrlStyle}" />
          </div>

          <div style="${fieldStyle}">
            <span style="${labelStyle}">Cuerpo</span>
            <textarea id="note-body" class="form-control" rows="5" maxlength="5000"
                      style="${ctrlStyle} resize:vertical; min-height:100px; font-family:inherit;">${escHtml(n.body || '')}</textarea>
          </div>

          <div style="display:flex; gap:12px;">
            <div style="${fieldStyle} flex:1;">
              <span style="${labelStyle}">Categoria</span>
              <select id="note-category" class="form-control" style="${ctrlStyle}">
                ${CATEGORIES.map(c => `<option value="${c}" ${n.category === c ? 'selected' : ''}>${CATEGORY_LABEL[c]}</option>`).join('')}
              </select>
            </div>
            <div style="${fieldStyle} flex:1;">
              <span style="${labelStyle}">Prioridad</span>
              <select id="note-priority" class="form-control" style="${ctrlStyle}">
                ${PRIORITIES.map(p => `<option value="${p}" ${n.priority === p ? 'selected' : ''}>${p}</option>`).join('')}
              </select>
            </div>
            <div style="${fieldStyle} flex:1;">
              <span style="${labelStyle}">Estado</span>
              <select id="note-status" class="form-control" style="${ctrlStyle}">
                ${STATUSES.map(s => `<option value="${s}" ${n.status === s ? 'selected' : ''}>${STATUS_LABEL[s]}</option>`).join('')}
              </select>
            </div>
          </div>

          <button id="note-save-btn" class="btn-primary" ${saving ? 'disabled' : ''}
                  style="align-self:flex-start; padding:8px 18px;">
            ${saving ? 'Guardando…' : '💾 Guardar cambios'}
          </button>
        </div>

        <hr style="margin:18px 0; border:none; border-top:1px solid #30363d;" />

        <div>
          <h4 style="margin:0 0 10px; color:#f0f6fc; font-size:0.95rem;">
            📎 Adjuntos <span style="color:#8b949e; font-weight:400;">(${(n.attachments || []).length})</span>
          </h4>
          <div class="note-attachments" style="display:flex; flex-wrap:wrap; gap:8px; margin-bottom:14px;">
            ${(n.attachments || []).map(a => renderAttachment(a)).join('') || '<span style="color:#6e7681; font-size:0.875rem;">Sin adjuntos todavia</span>'}
          </div>

          <div style="${fieldStyle}">
            <span style="${labelStyle}">Subir nuevo <span style="color:#6e7681; font-weight:400;">(max 5MB · png · jpg · webp · pdf · Ctrl+V para pegar screenshot)</span></span>
            <input type="file" id="note-upload-input" class="form-control"
                   accept="image/png,image/jpeg,image/webp,application/pdf"
                   capture="environment"
                   style="${ctrlStyle}" />
            <div style="font-size:0.875rem; color:#6e7681; margin-top:2px;">
              📷 En movil abre la camara directo · 📋 desktop: Ctrl+V pega screenshot del clipboard
            </div>
          </div>
        </div>
      </article>
    `;
  }

  function renderAttachment(a) {
    const isImage = a.mimeType?.startsWith('image/');
    const fileName = a.fileName || 'attachment';
    const shortName = fileName.length > 12 ? fileName.slice(0, 12) + '…' : fileName;

    // Acciones overlay (siempre disponibles): abrir + descargar.
    // Imagen: click en el thumbnail abre en pestana nueva (preview); descarga via boton inferior.
    // PDF/otro: click en el card abre en pestana nueva; descarga via boton inferior.
    const actionsOverlay = `
      <div class="att-actions"
           style="position:absolute; bottom:0; left:0; right:0; display:flex; gap:2px;
                  background:linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 70%, rgba(0,0,0,0) 100%);
                  padding:4px 4px 3px; opacity:0; transition:opacity 0.15s;
                  pointer-events:none;">
        <button type="button" class="att-btn-open"
                data-att-id="${escHtml(a.id)}" data-att-name="${escHtml(fileName)}"
                title="Abrir en pestana nueva"
                style="flex:1; background:rgba(31,111,235,0.85); border:none; color:#fff;
                       border-radius:3px; padding:3px 0; font-size:0.875rem; cursor:pointer;
                       pointer-events:auto;">
          ↗
        </button>
        <button type="button" class="att-btn-download"
                data-att-id="${escHtml(a.id)}" data-att-name="${escHtml(fileName)}"
                title="Descargar ${escHtml(fileName)}"
                style="flex:1; background:rgba(35,134,54,0.85); border:none; color:#fff;
                       border-radius:3px; padding:3px 0; font-size:0.875rem; cursor:pointer;
                       pointer-events:auto;">
          ⬇
        </button>
      </div>
    `;

    if (isImage) {
      return `
        <div class="att-thumb" data-att-id="${escHtml(a.id)}" data-att-name="${escHtml(fileName)}" data-att-clickable="${escHtml(a.id)}"
             style="width:88px; height:88px; background:#0d1117; border:1px solid #30363d;
                    border-radius:6px; overflow:hidden; position:relative; cursor:zoom-in;
                    transition:border-color 0.15s, transform 0.15s;"
             title="${escHtml(fileName)} · click para ampliar"
             onmouseover="this.style.borderColor='#58a6ff'; this.style.transform='scale(1.03)'; this.querySelector('.att-actions').style.opacity='1';"
             onmouseout="this.style.borderColor='#30363d'; this.style.transform='scale(1)'; this.querySelector('.att-actions').style.opacity='0';">
          <img data-att-id="${escHtml(a.id)}" src="" alt="${escHtml(fileName)}" loading="lazy"
               style="width:100%; height:100%; object-fit:cover;" />
          ${actionsOverlay}
        </div>
      `;
    }
    // PDF u otro: card clickeable que abre en pestana nueva. Boton ⬇ para descargar explicito.
    return `
      <div class="att-file" data-att-id="${escHtml(a.id)}" data-att-name="${escHtml(fileName)}"
           data-att-mime="${escHtml(a.mimeType || '')}"
           style="width:88px; height:88px; background:#0d1117; border:1px solid #30363d;
                  border-radius:6px; display:flex; flex-direction:column; align-items:center;
                  justify-content:center; gap:4px; cursor:pointer; position:relative;
                  transition:border-color 0.15s, transform 0.15s;"
           title="${escHtml(fileName)} · click para abrir"
           onmouseover="this.style.borderColor='#58a6ff'; this.style.transform='scale(1.03)'; this.querySelector('.att-actions').style.opacity='1';"
           onmouseout="this.style.borderColor='#30363d'; this.style.transform='scale(1)'; this.querySelector('.att-actions').style.opacity='0';">
        <span style="font-size:26px;">📄</span>
        <span style="font-size:0.875rem; color:#8b949e; text-align:center; padding:0 4px; word-break:break-all;">${escHtml(shortName)}</span>
        ${actionsOverlay}
      </div>
    `;
  }

  // ===== Lightbox: overlay full-size con swipe para attachments =====
  function _openLightbox(noteAttachments, startIdx = 0) {
    const images = noteAttachments.filter(a => a.mimeType?.startsWith('image/'));
    if (images.length === 0) return;

    let currentIdx = Math.max(0, images.findIndex(a => a.id === noteAttachments[startIdx]?.id));
    if (currentIdx < 0) currentIdx = 0;

    const overlay = document.createElement('div');
    overlay.id = 'nx-notes-lightbox';
    overlay.style.cssText = 'position:fixed; inset:0; background:rgba(0,0,0,0.92); z-index:1100; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:24px; backdrop-filter:blur(4px);';
    overlay.innerHTML = `
      <button data-lb-close style="position:absolute; top:16px; right:16px; background:rgba(255,255,255,0.1); border:none; color:#fff; width:36px; height:36px; border-radius:50%; cursor:pointer; font-size:1.2rem;">×</button>
      <button data-lb-prev style="position:absolute; left:16px; top:50%; transform:translateY(-50%); background:rgba(255,255,255,0.1); border:none; color:#fff; width:44px; height:44px; border-radius:50%; cursor:pointer; font-size:1.5rem;">‹</button>
      <button data-lb-next style="position:absolute; right:16px; top:50%; transform:translateY(-50%); background:rgba(255,255,255,0.1); border:none; color:#fff; width:44px; height:44px; border-radius:50%; cursor:pointer; font-size:1.5rem;">›</button>
      <img id="nx-lb-img" alt="" style="max-width:90vw; max-height:80vh; object-fit:contain; user-select:none; -webkit-user-drag:none;" />
      <div id="nx-lb-caption" style="margin-top:14px; color:#c9d1d9; font-size:0.875rem; text-align:center;"></div>
    `;
    document.body.appendChild(overlay);

    const $img = overlay.querySelector('#nx-lb-img');
    const $caption = overlay.querySelector('#nx-lb-caption');

    const loadCurrent = async () => {
      const att = images[currentIdx];
      $caption.textContent = `${att.fileName} · ${currentIdx + 1} / ${images.length}`;
      $img.style.opacity = '0.4';
      // #2495 NOTES T7: 410 GONE (attachment legacy) -> caption honesto, sin img rota.
      const meta = {};
      const url = await NotesApiClient.fetchAttachmentBlobUrl(att.noteId, att.id, { thumb: false, meta });
      if (url) {
        blobUrls.add(url);
        $img.src = url;
        $img.style.opacity = '1';
      } else {
        $img.removeAttribute('src');
        $img.style.opacity = '0';
        $caption.textContent = meta.status === 410
          ? `${att.fileName} · imagen no disponible (adjunto anterior a la migracion — re-subir)`
          : `${att.fileName} · no se pudo cargar`;
      }
    };

    const close = () => {
      overlay.remove();
      document.removeEventListener('keydown', onKey);
    };
    const next = () => { currentIdx = (currentIdx + 1) % images.length; loadCurrent(); };
    const prev = () => { currentIdx = (currentIdx - 1 + images.length) % images.length; loadCurrent(); };

    overlay.querySelector('[data-lb-close]').addEventListener('click', close);
    overlay.querySelector('[data-lb-next]').addEventListener('click', next);
    overlay.querySelector('[data-lb-prev]').addEventListener('click', prev);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    document.addEventListener('keydown', onKey);

    // Touch swipe (mobile)
    let touchStartX = 0;
    overlay.addEventListener('touchstart', (e) => { touchStartX = e.changedTouches[0].clientX; }, { passive: true });
    overlay.addEventListener('touchend',   (e) => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) > 50) { dx < 0 ? next() : prev(); }
    }, { passive: true });

    loadCurrent();
  }

  // ===== Helpers para abrir/descargar attachments =====
  async function _openAttachment(attId, fileName) {
    if (!selectedNote) return;
    _showToast('Abriendo…');
    const ok = await NotesApiClient.openAttachmentInNewTab(selectedNote.id, attId);
    if (!ok) _showToast('No se pudo abrir el adjunto', true);
  }

  async function _downloadAttachment(attId, fileName) {
    if (!selectedNote) return;
    _showToast(`Descargando ${fileName}…`);
    const ok = await NotesApiClient.downloadAttachment(selectedNote.id, attId, fileName);
    if (ok) _showToast('Descarga iniciada ✓');
    else _showToast('No se pudo descargar', true);
  }

  function bindEvents() {
    container.querySelector('#notes-new-btn')?.addEventListener('click', onNewNote);
    container.querySelector('#notes-flt-cat')?.addEventListener('change', e => {
      filterCategory = e.target.value; page = 1; reload();
    });
    container.querySelector('#notes-flt-status')?.addEventListener('change', e => {
      filterStatus = e.target.value; page = 1; reload();
    });
    container.querySelector('#notes-flt-priority')?.addEventListener('change', e => {
      filterPriority = e.target.value; page = 1; reload();
    });
    container.querySelectorAll('.note-card').forEach(card => {
      card.addEventListener('click', () => {
        const id = card.dataset.noteId;
        loadDetail(id);
      });
    });
    container.querySelector('#note-save-btn')?.addEventListener('click', onSave);
    container.querySelector('#note-delete-btn')?.addEventListener('click', onDelete);
    container.querySelector('#note-convert-btn')?.addEventListener('click', onConvertToMission);
    container.querySelector('#note-upload-input')?.addEventListener('change', onUpload);

    // ===== Attachments: imagenes -> lightbox; archivos -> abrir nueva pestana; botones -> handlers explicitos =====
    // Click en thumbnail (imagen) abre lightbox con swipe entre imagenes de la nota
    container.querySelectorAll('.att-thumb[data-att-clickable]').forEach(thumb => {
      thumb.addEventListener('click', (e) => {
        // Si el click viene de un boton interno (open/download), dejalo correr
        if (e.target.closest('.att-btn-open, .att-btn-download')) return;
        const id = thumb.dataset.attClickable;
        const attachments = selectedNote?.attachments || [];
        const startIdx = attachments.findIndex(a => a.id === id);
        if (startIdx >= 0) _openLightbox(attachments, startIdx);
      });
    });
    // Click en file card (PDF/otros) abre en pestana nueva (preview)
    container.querySelectorAll('.att-file').forEach(el => {
      el.addEventListener('click', (e) => {
        if (e.target.closest('.att-btn-open, .att-btn-download')) return;
        const attId = el.dataset.attId;
        const name = el.dataset.attName || 'attachment';
        if (attId) _openAttachment(attId, name);
      });
    });
    // Botones overlay open/download (siempre explicitos)
    container.querySelectorAll('.att-btn-open').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        _openAttachment(btn.dataset.attId, btn.dataset.attName || 'attachment');
      });
    });
    container.querySelectorAll('.att-btn-download').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        _downloadAttachment(btn.dataset.attId, btn.dataset.attName || 'attachment');
      });
    });
  }

  function onNewNote(opts = {}) {
    const businessId = getSelectedBusiness();
    const relatedArtefact = opts.relatedArtefact || null;
    // Layout vertical estricto: cada campo es un div con flex column (NO <label> nativo
    // que es inline y rompia el layout poniendo span e input lado a lado).
    // Inputs/selects/textarea: width:100% + box-sizing:border-box garantizan que llenen
    // el ancho del modal sin importar lo que .form-control herede.
    const fieldStyle = 'display:flex; flex-direction:column; gap:6px;';
    const labelStyle = 'font-size:0.875rem; color:#8b949e; font-weight:500;';
    const ctrlStyle  = 'width:100%; box-sizing:border-box;';

    const relatedChipHtml = relatedArtefact
      ? `<div style="display:flex; align-items:center; gap:6px; padding:8px 10px; background:#1f6feb22; border:1px solid #1f6feb55; border-radius:6px; font-size:0.875rem; color:#79c0ff;">
           <span>🔗 Vinculada a vista actual:</span>
           <code style="background:#0d1117; padding:1px 6px; border-radius:3px; font-size:0.875rem;">${escHtml(relatedArtefact)}</code>
         </div>`
      : '';

    const bodyHtml = `
      <form id="nx-note-create-form" style="display:flex; flex-direction:column; gap:14px;">
        ${relatedChipHtml}
        <div style="${fieldStyle}">
          <span style="${labelStyle} display:flex; justify-content:space-between; align-items:baseline;">
            <span>Titulo <span style="color:#f85149;">*</span></span>
            <span id="nx-note-create-title-counter" style="color:#6e7681; font-size:0.875rem;">0 / 200</span>
          </span>
          <input id="nx-note-create-title" class="form-control" required maxlength="200"
                 placeholder="Resume el bug, mejora o idea en una linea"
                 autocomplete="off" autofocus
                 style="${ctrlStyle}" />
        </div>

        <div style="${fieldStyle}">
          <span style="${labelStyle} display:flex; justify-content:space-between; align-items:baseline;">
            <span>Detalles <span style="color:#6e7681; font-weight:400;">(opcional, soporta saltos de linea)</span></span>
            <span id="nx-note-create-body-counter" style="color:#6e7681; font-size:0.875rem;">0 / 5000</span>
          </span>
          <textarea id="nx-note-create-body" class="form-control" rows="5" maxlength="5000"
                    placeholder="Pasos para reproducir, contexto, referencias, capturas..."
                    style="${ctrlStyle} resize:vertical; min-height:100px; font-family:inherit;"></textarea>
        </div>

        <div style="display:flex; gap:12px;">
          <div style="${fieldStyle} flex:1;">
            <span style="${labelStyle}">Categoria</span>
            <select id="nx-note-create-category" class="form-control" style="${ctrlStyle}">
              ${CATEGORIES.map(c => `<option value="${c}" ${c === 'general' ? 'selected' : ''}>${CATEGORY_LABEL[c]}</option>`).join('')}
            </select>
          </div>
          <div style="${fieldStyle} flex:1;">
            <span style="${labelStyle}">Prioridad</span>
            <select id="nx-note-create-priority" class="form-control" style="${ctrlStyle}">
              ${PRIORITIES.map(p => `<option value="${p}" ${p === 'medium' ? 'selected' : ''}>${p}</option>`).join('')}
            </select>
          </div>
        </div>

        <div style="${fieldStyle}">
          <span style="${labelStyle}">Tags <span style="color:#6e7681; font-weight:400;">(separados por coma o Enter)</span></span>
          <div id="nx-note-create-chips"
               style="display:flex; flex-wrap:wrap; gap:6px; min-height:34px; padding:6px 8px;
                      background:#0d1117; border:1px solid #30363d; border-radius:6px; align-items:center;">
            <input id="nx-note-create-tags" type="text"
                   placeholder="ej: panel, desktop, urgente"
                   autocomplete="off"
                   style="flex:1; min-width:120px; background:transparent; border:none; outline:none;
                          color:#c9d1d9; font-size:0.88rem; padding:4px 0;" />
          </div>
        </div>

        <div style="display:flex; align-items:center; gap:8px; padding:8px 10px; background:#0d1117; border:1px dashed #30363d; border-radius:6px; font-size:0.875rem; color:#8b949e;">
          <span style="font-size:1rem;">📋</span>
          <span>Ctrl+V para pegar screenshots desde portapapeles · se adjuntan automaticamente al guardar</span>
        </div>

        <div id="nx-note-create-pending-attachments" style="display:none; flex-direction:column; gap:6px;">
          <span style="font-size:0.875rem; color:#8b949e; font-weight:500;">📎 Adjuntos pendientes (se subiran tras crear la nota)</span>
          <div id="nx-note-create-pending-list" style="display:flex; flex-wrap:wrap; gap:6px;"></div>
        </div>

        <div id="nx-note-create-error" role="alert"
             style="display:none; padding:10px 12px; background:#3c1e1e; border:1px solid #f85149; border-radius:6px;
                    color:#ffa198; font-size:0.875rem; line-height:1.4;"></div>
      </form>
    `;
    const footerHtml = `
      <button id="nx-note-create-cancel" class="btn-secondary">Cancelar</button>
      <button id="nx-note-create-submit" class="btn-primary">Crear nota</button>
    `;
    _openModal({ title: 'Nueva nota', bodyHtml, footerHtml, maxWidth: '580px' });

    const $titleInput = document.getElementById('nx-note-create-title');
    const $titleCounter = document.getElementById('nx-note-create-title-counter');
    const $bodyInput = document.getElementById('nx-note-create-body');
    const $bodyCounter = document.getElementById('nx-note-create-body-counter');
    const $tagsInput = document.getElementById('nx-note-create-tags');
    const $chipsBox = document.getElementById('nx-note-create-chips');
    const $err  = document.getElementById('nx-note-create-error');
    const $btn  = document.getElementById('nx-note-create-submit');
    const $cancel = document.getElementById('nx-note-create-cancel');
    setTimeout(() => $titleInput?.focus(), 50);

    // Counters live
    const updateCounter = ($el, $counter, max) => {
      const len = ($el?.value || '').length;
      if ($counter) {
        $counter.textContent = `${len} / ${max}`;
        $counter.style.color = len > max * 0.9 ? '#d29922' : '#6e7681';
      }
    };
    $titleInput?.addEventListener('input', () => updateCounter($titleInput, $titleCounter, 200));
    $bodyInput?.addEventListener('input',  () => updateCounter($bodyInput,  $bodyCounter,  5000));

    // Tags como chips: Enter o coma agrega chip, Backspace en input vacio borra ultimo
    const tagsState = [];
    const renderChips = () => {
      // Remover todos los chips existentes (no el input)
      [...$chipsBox.querySelectorAll('.nx-chip')].forEach(c => c.remove());
      tagsState.forEach((tag, idx) => {
        const chip = document.createElement('span');
        chip.className = 'nx-chip';
        chip.style.cssText = 'background:#1f6feb33; color:#79c0ff; padding:2px 8px; border-radius:10px; font-size:0.875rem; display:inline-flex; align-items:center; gap:4px;';
        chip.innerHTML = `${escHtml(tag)} <button type="button" data-rm-idx="${idx}" style="background:none; border:none; color:#79c0ff; cursor:pointer; padding:0; font-size:1rem; line-height:1;">×</button>`;
        $chipsBox.insertBefore(chip, $tagsInput);
        chip.querySelector('button')?.addEventListener('click', (e) => {
          e.preventDefault();
          tagsState.splice(idx, 1);
          renderChips();
        });
      });
    };
    const commitTag = () => {
      const v = ($tagsInput.value || '').trim().replace(/,$/, '').trim();
      if (v && !tagsState.includes(v)) {
        tagsState.push(v);
        renderChips();
      }
      $tagsInput.value = '';
    };
    $tagsInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        commitTag();
      } else if (e.key === 'Backspace' && !$tagsInput.value && tagsState.length > 0) {
        tagsState.pop();
        renderChips();
      }
    });
    $tagsInput?.addEventListener('blur', () => { if ($tagsInput.value.trim()) commitTag(); });

    // ===== PASTE SCREENSHOTS clipboard ⭐ =====
    // Detecta Ctrl+V con imagen en clipboard. Las imagenes se acumulan en pendingAttachments
    // y se suben en bucle DESPUES de que la nota se cree (necesitamos noteId del response).
    const pendingAttachments = []; // File[]
    const $pendingBox = document.getElementById('nx-note-create-pending-attachments');
    const $pendingList = document.getElementById('nx-note-create-pending-list');
    const renderPending = () => {
      if (pendingAttachments.length === 0) {
        $pendingBox.style.display = 'none';
        $pendingList.innerHTML = '';
        return;
      }
      $pendingBox.style.display = 'flex';
      $pendingList.innerHTML = pendingAttachments.map((f, idx) => {
        const url = URL.createObjectURL(f);
        blobUrls.add(url);
        return `
          <div style="position:relative; width:60px; height:60px; border:1px solid #30363d; border-radius:4px; overflow:hidden;">
            <img src="${url}" loading="lazy" style="width:100%; height:100%; object-fit:cover;" alt="${escHtml(f.name || 'screenshot')}" />
            <button type="button" data-pending-rm="${idx}"
                    style="position:absolute; top:2px; right:2px; background:rgba(0,0,0,0.7); color:#fff; border:none; border-radius:50%; width:18px; height:18px; font-size:0.875rem; cursor:pointer; display:flex; align-items:center; justify-content:center;"
                    title="Remover">×</button>
          </div>
        `;
      }).join('');
      $pendingList.querySelectorAll('[data-pending-rm]').forEach(btn => {
        btn.addEventListener('click', () => {
          const idx = parseInt(btn.dataset.pendingRm, 10);
          pendingAttachments.splice(idx, 1);
          renderPending();
        });
      });
    };

    const onPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            if (file.size > 5 * 1024 * 1024) {
              showInlineError(`Imagen pegada excede 5MB (${(file.size/1024/1024).toFixed(2)}MB)`);
              continue;
            }
            pendingAttachments.push(file);
            renderPending();
            _showToast(`Screenshot agregado (${pendingAttachments.length} pendiente${pendingAttachments.length === 1 ? '' : 's'})`);
          }
          e.preventDefault();
          break;
        }
      }
    };
    document.addEventListener('paste', onPaste);
    // Cleanup: cuando el modal cierra, remover el listener
    const $modalRoot = document.getElementById('nx-notes-modal-root');
    const cleanupPaste = new MutationObserver(() => {
      if ($modalRoot.hidden) {
        document.removeEventListener('paste', onPaste);
        cleanupPaste.disconnect();
      }
    });
    cleanupPaste.observe($modalRoot, { attributes: true, attributeFilter: ['hidden'] });

    const showInlineError = (msg) => {
      if (!$err) return;
      $err.textContent = msg;
      $err.style.display = 'block';
    };
    const clearInlineError = () => { if ($err) $err.style.display = 'none'; };

    const submit = async () => {
      clearInlineError();
      const title = ($titleInput?.value || '').trim();
      if (!title) {
        showInlineError('El titulo es requerido');
        $titleInput?.focus();
        return;
      }
      // Capturar el ultimo tag pendiente en el input
      if ($tagsInput.value.trim()) commitTag();

      const body     = document.getElementById('nx-note-create-body')?.value || '';
      const category = document.getElementById('nx-note-create-category')?.value || 'general';
      const priority = document.getElementById('nx-note-create-priority')?.value || 'medium';
      const tags     = tagsState.slice();

      $btn.disabled = true;
      $btn.textContent = 'Creando…';
      $cancel.disabled = true;

      const metadata = relatedArtefact ? { related_artefact: relatedArtefact } : undefined;

      const res = await NotesApiClient.create({
        title,
        body: body.trim() || undefined,
        category,
        priority,
        tags: tags.length ? tags : undefined,
        businessId: businessId || undefined,
        metadata
      });

      if (res.ok) {
        const payload = res.data?.data ?? res.data;
        const newNoteId = payload?.id;

        // Subir attachments pendientes (paste) tras crear la nota
        if (newNoteId && pendingAttachments.length > 0) {
          $btn.textContent = `Subiendo ${pendingAttachments.length} adjunto(s)…`;
          for (const file of pendingAttachments) {
            try { await NotesApiClient.uploadAttachment(newNoteId, file); }
            catch (uErr) { console.warn('[NotesView] upload pending failed', uErr); }
          }
        }

        _closeModal();
        _showToast(`Nota creada${pendingAttachments.length > 0 ? ` con ${pendingAttachments.length} adjunto(s)` : ''} ✓`);
        await reload();
        if (newNoteId) await loadDetail(newNoteId);
      } else {
        $btn.disabled = false;
        $btn.textContent = 'Crear nota';
        $cancel.disabled = false;
        showInlineError('No se pudo crear: ' + _parseError(res));
      }
    };

    $btn?.addEventListener('click', submit);
    document.getElementById('nx-note-create-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      submit();
    });
    $cancel?.addEventListener('click', () => _closeModal());
  }

  async function onSave() {
    if (!selectedNote || saving) return;

    // BUG FIX: leer DOM values ANTES de cualquier render.
    // Antes: saving=true; render(); querySelector('.value') -> render() reescribe innerHTML
    // con los valores ORIGINALES de selectedNote, asi el value que se enviaba al backend
    // era el viejo, NO lo que el user typeo. Cambios "se perdian" tras guardar.
    const $titleEl    = container.querySelector('#note-title');
    const $bodyEl     = container.querySelector('#note-body');
    const $categoryEl = container.querySelector('#note-category');
    const $priorityEl = container.querySelector('#note-priority');
    const $statusEl   = container.querySelector('#note-status');
    if (!$titleEl) return; // detail no montado

    const payload = {
      title:    ($titleEl.value || '').trim(),
      body:     $bodyEl?.value ?? '',
      category: $categoryEl?.value ?? selectedNote.category,
      priority: $priorityEl?.value ?? selectedNote.priority,
      status:   $statusEl?.value ?? selectedNote.status
    };

    if (!payload.title) {
      _showToast('El titulo no puede estar vacio', true);
      $titleEl.focus();
      return;
    }

    saving = true;
    // Actualizar boton sin re-render (evita perder estado del DOM mientras request corre)
    const $saveBtn = container.querySelector('#note-save-btn');
    if ($saveBtn) {
      $saveBtn.disabled = true;
      $saveBtn.textContent = 'Guardando…';
    }

    const res = await NotesApiClient.update(selectedNote.id, payload);
    saving = false;

    if (res.ok) {
      _showToast('Cambios guardados ✓');
      // Mergear lo guardado en selectedNote ANTES de re-render (asi el detail muestra
      // los valores nuevos aunque loadDetail no devuelva todo o falle).
      selectedNote = { ...selectedNote, ...payload };
      await loadDetail(selectedNote.id);
      await reload();
    } else {
      _showToast('No se pudo guardar: ' + _parseError(res), true);
      // Restaurar boton sin reset del form (preserva lo que el user typeo)
      if ($saveBtn) {
        $saveBtn.disabled = false;
        $saveBtn.textContent = '💾 Guardar cambios';
      }
    }
  }

  /**
   * Convierte la nota seleccionada a mision pre-rellenando el form de #/missions:
   * 1. Guarda payload en sessionStorage (consumido 1 vez por MissionListView).
   * 2. Navega a #/missions; el form-area se abre auto y los inputs vienen rellenos.
   * 3. Tras crear la mision, MissionListView vincula la nota via PATCH metadata.
   *    El chip "Convertida a mision X" aparece al volver a #/notas.
   */
  function onConvertToMission() {
    if (!selectedNote) return;
    const n = selectedNote;
    const attachmentNames = (n.attachments || [])
      .map(a => `- ${a.fileName || 'attachment'} (${a.mimeType || 'unknown'})`);
    const descriptionParts = [];
    if (n.body) descriptionParts.push(n.body);
    if (attachmentNames.length > 0) {
      descriptionParts.push('');
      descriptionParts.push(`📎 ${attachmentNames.length} adjunto(s) en nota origen:`);
      descriptionParts.push(...attachmentNames);
    }
    descriptionParts.push('');
    descriptionParts.push(`[Origen: nota ${n.id}]`);

    // existingMetadata: enviar la metadata actual de la nota para que MissionListView
    // pueda hacer merge antes del PATCH (PATCH sobrescribe metadata, no hace merge).
    const existingMetadata = (n.metadata && typeof n.metadata === 'object') ? n.metadata : {};
    const prefill = {
      noteId:      n.id,
      title:       n.title,
      description: descriptionParts.join('\n'),
      priority:    n.priority || 'medium',
      suggestedCode: `NOTE-${(n.id || '').slice(0, 8).toUpperCase()}`,
      attachmentCount: attachmentNames.length,
      existingMetadata
    };
    sessionStorage.setItem('nx_mission_prefill', JSON.stringify(prefill));
    _showToast('Abriendo form de nueva mision…');
    window.location.hash = '#/missions';
  }

  function onDelete() {
    if (!selectedNote) return;
    const noteTitle = selectedNote.title;
    const noteId = selectedNote.id;
    const bodyHtml = `
      <p style="margin:0 0 8px; color:#c9d1d9;">¿Seguro que quieres descartar la nota?</p>
      <p style="margin:0 0 8px; color:#f0f6fc; font-weight:600;">"${escHtml(noteTitle)}"</p>
      <p style="margin:0; font-size:0.875rem; color:#8b949e;">
        Soft delete (status=<code style="background:#21262d;padding:1px 5px;border-radius:3px;">discarded</code>) — la nota no se borra fisicamente, podes restaurarla cambiando el status despues.
      </p>
    `;
    const footerHtml = `
      <button id="nx-note-del-cancel" class="btn-secondary">Cancelar</button>
      <button id="nx-note-del-confirm" class="btn-primary"
              style="background:#da3633; border-color:#da3633;">🗑 Confirmar</button>
    `;
    _openModal({ title: 'Descartar nota', bodyHtml, footerHtml });

    const $cancel = document.getElementById('nx-note-del-cancel');
    const $confirm = document.getElementById('nx-note-del-confirm');

    $cancel?.addEventListener('click', () => _closeModal());
    $confirm?.addEventListener('click', async () => {
      $confirm.disabled = true;
      $confirm.textContent = 'Descartando…';
      $cancel.disabled = true;
      const res = await NotesApiClient.remove(noteId);
      if (res.ok) {
        _closeModal();
        _showToast('Nota descartada ✓');
        selectedNote = null;
        await reload();
      } else {
        _closeModal();
        _showToast('No se pudo descartar: ' + _parseError(res), true);
      }
    });
  }

  async function onUpload(e) {
    if (!selectedNote) return;
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      _showToast('Archivo excede 5MB', true);
      e.target.value = '';
      return;
    }
    const res = await NotesApiClient.uploadAttachment(selectedNote.id, file);
    if (res.ok) {
      _showToast('Adjunto subido ✓');
      await loadDetail(selectedNote.id);
    } else {
      _showToast('No se pudo subir: ' + _parseError(res), true);
    }
    e.target.value = '';
  }

  // ==================== UI helpers (toast + modal) ====================
  // Patron toast tomado de EquiposCatalogView.js:67. Modal local porque NO existe NxModal compartido en v2.2/panel/src/presentation/components/.
  // Toast + modal viven en document.body (NO en container.innerHTML) para que sobrevivan a re-renders.

  function _ensureToastEl() {
    let $t = document.getElementById('nx-notes-toast');
    if ($t) return $t;
    $t = document.createElement('div');
    $t.id = 'nx-notes-toast';
    $t.hidden = true;
    $t.setAttribute('role', 'status');
    $t.setAttribute('aria-live', 'polite');
    $t.style.cssText = 'position:fixed; bottom:24px; right:24px; padding:10px 14px; color:#fff; z-index:1100; border-radius:6px; font-size:0.88rem; box-shadow:0 4px 12px rgba(0,0,0,0.4);';
    document.body.appendChild($t);
    return $t;
  }

  function _showToast(msg, isError = false) {
    const $t = _ensureToastEl();
    $t.textContent = msg;
    $t.style.background = isError ? '#f85149' : '#238636';
    $t.hidden = false;
    clearTimeout($t._timer);
    $t._timer = setTimeout(() => { $t.hidden = true; }, 2800);
  }

  function _ensureModalRoot() {
    let $root = document.getElementById('nx-notes-modal-root');
    if ($root) return $root;
    $root = document.createElement('div');
    $root.id = 'nx-notes-modal-root';
    $root.hidden = true;
    document.body.appendChild($root);
    return $root;
  }

  let _modalEscHandler = null;
  function _openModal({ title = '', bodyHtml = '', footerHtml = '', maxWidth = '520px' } = {}) {
    const $root = _ensureModalRoot();
    $root.innerHTML = `
      <div class="nx-modal-backdrop"
           style="position:fixed; inset:0; background:rgba(0,0,0,0.65); z-index:1050;
                  display:flex; align-items:center; justify-content:center; padding:24px;
                  backdrop-filter:blur(2px);">
        <div class="nx-modal-card" role="dialog" aria-modal="true"
             style="background:#161b22; border:1px solid #30363d; border-radius:10px;
                    width:100%; max-width:${maxWidth}; max-height:90vh; display:flex; flex-direction:column;
                    box-shadow:0 8px 32px rgba(0,0,0,0.5);">
          <header style="display:flex; align-items:center; justify-content:space-between;
                         padding:14px 18px; border-bottom:1px solid #30363d;">
            <h3 style="margin:0; color:#f0f6fc; font-size:1.05rem;">${escHtml(title)}</h3>
            <button class="nx-modal-close" aria-label="Cerrar"
                    style="background:transparent; border:none; color:#8b949e; font-size:1.3rem;
                           cursor:pointer; padding:0 6px; transition:color 0.15s;"
                    onmouseover="this.style.color='#f0f6fc'"
                    onmouseout="this.style.color='#8b949e'">×</button>
          </header>
          <div class="nx-modal-body" style="padding:18px; overflow-y:auto;">${bodyHtml}</div>
          <footer style="display:flex; gap:8px; justify-content:flex-end;
                         padding:12px 18px; border-top:1px solid #30363d;">${footerHtml}</footer>
        </div>
      </div>
    `;
    $root.hidden = false;

    const close = () => _closeModal();
    $root.querySelector('.nx-modal-close')?.addEventListener('click', close);
    $root.querySelector('.nx-modal-backdrop')?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) close();
    });
    _modalEscHandler = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', _modalEscHandler);
  }

  function _closeModal() {
    const $root = document.getElementById('nx-notes-modal-root');
    if (!$root) return;
    $root.hidden = true;
    $root.innerHTML = '';
    if (_modalEscHandler) {
      document.removeEventListener('keydown', _modalEscHandler);
      _modalEscHandler = null;
    }
  }

  /**
   * Extrae un mensaje accionable del error del ApiBase / fetch fallback.
   * Fallback chain: message -> detail -> title -> code -> JSON stringify (200 chars).
   * REDIRECT #494: antes solo se mostraba "error desconocido" cuando no habia .message.
   */
  function _parseError(res, fallback = 'error desconocido') {
    const e = res?.error;
    if (!e) return fallback;
    if (typeof e === 'string') return e;
    if (e.message) return e.message;
    if (e.detail)  return e.detail;
    if (e.title)   return e.title;
    if (e.code)    return String(e.code);
    try { return JSON.stringify(e).slice(0, 200); }
    catch { return fallback; }
  }

  function _cleanupBodyEls() {
    document.getElementById('nx-notes-toast')?.remove();
    const $modal = document.getElementById('nx-notes-modal-root');
    if ($modal) {
      $modal.remove();
      if (_modalEscHandler) {
        document.removeEventListener('keydown', _modalEscHandler);
        _modalEscHandler = null;
      }
    }
  }

  function formatDate(iso) {
    if (!iso) return '';
    return formatearFecha(iso, { dateStyle: 'short', timeStyle: 'short' }, { parte: 'todo' });
  }

  // ===== PASTE handler en detail panel (cuando hay nota seleccionada) =====
  // Si NO hay modal abierto y SI hay selectedNote, Ctrl+V con imagen sube directo a esa nota.
  const onDetailPaste = async (e) => {
    if (!selectedNote) return;
    // Si el modal create esta abierto, su propio paste handler maneja el evento (ya tiene preventDefault)
    const modalRoot = document.getElementById('nx-notes-modal-root');
    if (modalRoot && !modalRoot.hidden) return;
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          if (file.size > 5 * 1024 * 1024) {
            _showToast(`Screenshot pegado excede 5MB (${(file.size/1024/1024).toFixed(2)}MB)`, true);
            return;
          }
          _showToast('Subiendo screenshot pegado…');
          const res = await NotesApiClient.uploadAttachment(selectedNote.id, file);
          if (res.ok) {
            _showToast('Screenshot adjuntado ✓');
            await loadDetail(selectedNote.id);
          } else {
            _showToast('No se pudo adjuntar: ' + _parseError(res), true);
          }
        }
        e.preventDefault();
        break;
      }
    }
  };
  document.addEventListener('paste', onDetailPaste);

  // ===== Listener para FAB global (NxFloatingButton) =====
  const onOpenCreateEvent = (e) => {
    const opts = e?.detail || {};
    onNewNote(opts);
  };
  window.addEventListener('nx-notes:open-create', onOpenCreateEvent);

  // Cleanup blob URLs + toast/modal + listeners cuando la vista se destruye
  const observer = new MutationObserver(() => {
    if (!container.isConnected) {
      blobUrls.forEach(url => URL.revokeObjectURL(url));
      blobUrls.clear();
      thumbCache.clear();
      _cleanupBodyEls();
      document.removeEventListener('paste', onDetailPaste);
      window.removeEventListener('nx-notes:open-create', onOpenCreateEvent);
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Primer load
  reload().then(() => {
    // Si el FAB seteo la flag _nxNotesAutoCreate antes de navegar, abrir modal create con related
    if (window._nxNotesAutoCreate) {
      const opts = window._nxNotesAutoCreate;
      delete window._nxNotesAutoCreate;
      setTimeout(() => onNewNote(opts), 150);
    }
  });

  return container;
}
